import { buttonTextReadable, firstWordOf, FONTS, READY_MADE } from "../../../server/rules/branding";
import { shade, TEXT_LABEL } from "../../shell/brand";
import type { Branding, ScreenContext, TextSize } from "../../shell/types";
import { activityPanel } from "../../ui/activity";
import { autosaveStatus, createAutosave } from "../../ui/autosave";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { uploadControl } from "../../ui/upload";
import { pageHead } from "../empty";

// Settings › Branding (mockup): the app name and logo, the company's default text size, font (one
// of the list or an uploaded font file) and two colors, with a live preview. The logo and font
// file go up with the upload control (app/ui/upload.ts, brief 1A-7) and are used at once.
// Every change saves on its own after a short pause (app/ui/autosave.ts) and changes the app
// for everyone in this company only; each save is in the history, shown in the activity panel.

const DEFAULTS = {
  primaryColor: "#073E5A",
  accentColor: "#4096AB",
  font: "Inter",
  textSize: "standard" as TextSize,
};
const FONT_LABEL: Record<string, string> = {
  Inter: "Inter (default)",
  "Atkinson Hyperlegible": "Atkinson Hyperlegible (easy reading)",
};
const SCALE: Record<TextSize, string> = { small: ".85em", standard: "1em", large: "1.2em" };
/** The font list's value for the uploaded font. */
const UPLOADED = "uploaded";

/** The two colors most found in an image, as the mockup's "Pull colors from your logo". */
function colorsOf(img: HTMLImageElement): [string, string] | null {
  const c = document.createElement("canvas");
  c.width = c.height = 48;
  const g = c.getContext("2d");
  if (!g) return null;
  g.drawImage(img, 0, 0, 48, 48);
  const d = g.getImageData(0, 0, 48, 48).data;
  const bins = new Map<string, { n: number; r: number; g: number; b: number }>();
  for (let i = 0; i < d.length; i += 4) {
    const [r, gg, b, a] = [d[i] ?? 0, d[i + 1] ?? 0, d[i + 2] ?? 0, d[i + 3] ?? 0];
    if (a < 128) continue;
    const mx = Math.max(r, gg, b);
    const mn = Math.min(r, gg, b);
    if ((mx > 235 && mn > 235) || mx < 20) continue;
    const key = [r, gg, b].map((v) => v >> 5).join();
    const o = bins.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
    o.n++;
    o.r += r;
    o.g += gg;
    o.b += b;
    bins.set(key, o);
  }
  const hex = (v: number) => Math.round(v).toString(16).padStart(2, "0");
  const found = [...bins.values()]
    .sort((x, y) => y.n - x.n)
    .map((o) => `#${hex(o.r / o.n)}${hex(o.g / o.n)}${hex(o.b / o.n)}`.toUpperCase());
  if (!found[0]) return null;
  return [shade(found[0], -0.35).toUpperCase(), (found[1] ?? shade(found[0], 0.25)).toUpperCase()];
}

export async function brandingScreen(host: HTMLElement, ctx: ScreenContext) {
  const res = await api<{ company: { name: string }; id: string | null; branding: Branding }>(
    "GET",
    "/api/settings/branding",
  );
  if (!res.ok) {
    host.append(
      el("p", { class: "note", role: "alert" }, res.body.error ?? "Branding couldn't be loaded."),
    );
    return;
  }
  const saved = res.body.branding;
  let draft: Branding = { ...saved };
  const company = res.body.company.name;

  const preview = () => ctx.applyBranding(draft, ctx.me.user.textSize ?? draft.textSize);

  // ---- Autosave and the history
  const activity = res.body.id ? activityPanel("branding", res.body.id, { autosaved: true }) : null;
  const status = autosaveStatus();
  const saver = createAutosave({
    save: async () => {
      const put = await api<{ branding: Branding }>("PUT", "/api/settings/branding", {
        appName: draft.appName,
        primaryColor: draft.primaryColor,
        accentColor: draft.accentColor,
        font: draft.font,
        fontFileId: draft.fontFile?.id ?? null,
        textSize: draft.textSize,
        shortName: draft.shortName ?? null,
        topBar: draft.topBar,
      });
      return put.ok ? { ok: true } : { ok: false, error: put.body.error };
    },
    onStatus: status.show,
    onSaved: async () => {
      await ctx.refresh();
      // The shell puts back what's saved; what's on screen may already be newer.
      if (host.isConnected) preview();
      await activity?.refresh();
    },
  });
  const changed = () => saver.changed();

  // ---- Name and logo
  const name = el("input", {
    type: "text",
    id: "bname",
    maxlength: "40",
    value: draft.appName,
    autocomplete: "off",
  });
  name.addEventListener("input", () => {
    draft.appName = name.value;
    changed();
  });

  // ---- Logo: uploaded, then used at once (top bar for everyone in this company)
  const logoPrev = el("div", { class: "logo-prev" });
  const drawLogo = (src: string | null) => {
    logoPrev.replaceChildren(src ? el("img", { src, alt: "Company logo" }) : "No logo");
    draft.logo = src;
    removeLogo.hidden = !src;
    drawTopBar();
  };
  /** Saves a logo or font file at once (not with the other changes), then redraws the app. */
  const useFile = async (
    change: { logoFileId?: string | null; fontFileId?: string },
    done: string,
  ) => {
    const put = await api<{ branding: Branding }>("PUT", "/api/settings/branding", change);
    if (!put.ok) return toast("That wasn't saved", put.body.error);
    await ctx.refresh();
    if (!host.isConnected) return;
    const now = put.body.branding;
    if ("logoFileId" in change) drawLogo(now.logo);
    if (change.fontFileId) {
      draft.fontFile = now.fontFile;
      drawFonts();
    }
    preview();
    toast(done);
    await activity?.refresh();
  };
  const logoUpload = res.body.id
    ? uploadControl({
        kind: "branding",
        id: res.body.id,
        purpose: "logo",
        label: "Upload logo",
        camera: true,
        hint: "PNG, JPG or WebP, up to 4 MB. Shown top left.",
        onUploaded: (f) => useFile({ logoFileId: f.id }, "Logo uploaded"),
      })
    : null;

  // ---- Top bar (brief 1D-3, mockup v40): the logo or initials, a short name, logo only
  const removeLogo = el(
    "button",
    {
      type: "button",
      class: "btn ib sm",
      "aria-label": "Remove logo (shows the company initials)",
      "data-tip": "Remove logo (shows the company initials)",
    },
    icon("trash"),
  );
  removeLogo.addEventListener("click", () => void useFile({ logoFileId: null }, "Logo removed"));
  const shortName = el("input", {
    type: "text",
    id: "tb-name",
    maxlength: "20",
    value: draft.shortName ?? "",
    placeholder: firstWordOf(company),
    autocomplete: "off",
  });
  shortName.addEventListener("input", () => {
    draft.shortName = shortName.value.trim() || null;
    drawTopBar();
    changed();
  });
  const shows = el(
    "div",
    { class: "seg", role: "radiogroup", "aria-label": "Show" },
    ...(
      [
        ["logo_name", "Logo and short name"],
        ["logo", "Logo only"],
      ] as const
    ).map(([value, label]) => {
      const input = el("input", {
        type: "radio",
        name: "tbs",
        value,
        checked: draft.topBar === value,
      });
      input.addEventListener("change", () => {
        draft.topBar = value;
        drawTopBar();
        changed();
      });
      return el("label", {}, input, label);
    }),
  );
  const initials = company
    .split(/\s+/)
    .filter((w) => /^[A-Za-z0-9]/.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const topPrev = el("div", { class: "tbprev" });
  function drawTopBar() {
    const name = draft.shortName ?? firstWordOf(company);
    const mark = (logo: string | null) =>
      logo
        ? el("img", { src: logo, alt: "", class: "tblogo" })
        : el("span", { class: "brandmark", "aria-hidden": "true" }, initials);
    const row = (logo: string | null, note: string) =>
      el(
        "div",
        { class: "tbrow" },
        mark(logo),
        draft.topBar === "logo" && logo ? "" : el("b", {}, name),
        el("span", { class: "small muted", style: "margin-left:auto" }, note),
      );
    topPrev.replaceChildren(
      ...(draft.logo ? [row(draft.logo, "with the logo")] : []),
      row(null, "no logo yet"),
      el("span", { class: "small muted" }, `Full name on hover: ${company}`),
    );
  }
  drawLogo(saved.logo);

  // ---- Text size
  const sizes = el(
    "div",
    { class: "seg big", role: "group", "aria-label": "Default text size" },
    ...(["small", "standard", "large"] as TextSize[]).map((size) => {
      const b = el(
        "button",
        {
          type: "button",
          "data-size": size,
          "aria-pressed": draft.textSize === size ? "true" : "false",
        },
        el("span", { style: `font-size:${SCALE[size]}` }, "Aa"),
        ` ${TEXT_LABEL[size]}`,
      );
      b.addEventListener("click", () => {
        draft.textSize = size;
        for (const x of sizes.querySelectorAll<HTMLElement>("button"))
          x.setAttribute("aria-pressed", x.dataset.size === size ? "true" : "false");
        preview();
        changed();
      });
      return b;
    }),
  );

  // ---- Font: one of the list, or the company's uploaded font file (mockup "(uploaded)")
  const font = el("select", { id: "bfont" });
  let uploadedFont = draft.fontFile;
  const drawFonts = () => {
    if (draft.fontFile) uploadedFont = draft.fontFile;
    font.replaceChildren(
      ...FONTS.map((f) =>
        el(
          "option",
          { value: f, selected: !draft.fontFile && draft.font === f },
          FONT_LABEL[f] ?? f,
        ),
      ),
      ...(uploadedFont
        ? [
            el(
              "option",
              { value: UPLOADED, selected: !!draft.fontFile },
              `${uploadedFont.name} (uploaded)`,
            ),
          ]
        : []),
    );
  };
  drawFonts();
  font.addEventListener("change", () => {
    if (font.value === UPLOADED) draft.fontFile = uploadedFont;
    else {
      draft.font = font.value;
      draft.fontFile = null;
    }
    preview();
    changed();
  });
  const fontUpload = res.body.id
    ? uploadControl({
        kind: "branding",
        id: res.body.id,
        purpose: "font",
        label: "Upload a font",
        hint: "WOFF2, TTF or OTF. The whole app switches to it.",
        onUploaded: (f) => useFile({ fontFileId: f.id }, "Font uploaded"),
      })
    : null;

  // ---- Colors
  const check = el("p", { class: "small", id: "bcheck" });
  const drawCheck = () =>
    check.replaceChildren(
      buttonTextReadable(draft.primaryColor)
        ? el("span", { class: "chip ok" }, "Text on buttons is readable")
        : el(
            "span",
            { class: "chip warn" },
            "Text on buttons may be hard to read; pick a darker main color",
          ),
    );
  const colorField = (key: "primaryColor" | "accentColor", id: string, label: string) => {
    const input = el("input", { type: "color", id, value: draft[key] });
    const code = el("code", {}, draft[key]);
    input.addEventListener("input", () => {
      draft[key] = input.value.toUpperCase();
      code.textContent = draft[key];
      drawCheck();
      preview();
      changed();
    });
    return {
      input,
      code,
      field: el(
        "div",
        { class: "field" },
        el("label", { for: id }, label),
        el("span", { class: "colorpick" }, input, code),
      ),
    };
  };
  const primary = colorField("primaryColor", "bprim", "Main color");
  const accent = colorField("accentColor", "bacc", "Accent color");
  const setColors = (p: string, a: string) => {
    draft.primaryColor = p;
    draft.accentColor = a;
    primary.input.value = p.toLowerCase();
    primary.code.textContent = p;
    accent.input.value = a.toLowerCase();
    accent.code.textContent = a;
    drawCheck();
    preview();
    changed();
  };
  const ready = el(
    "div",
    { class: "linkrow" },
    ...READY_MADE.map((r) => {
      const b = el(
        "button",
        { type: "button", class: "chipbtn" },
        el("span", { class: "sw", style: `background:${r.primary}` }),
        el("span", { class: "sw", style: `background:${r.accent}` }),
        ` ${r.name}`,
      );
      b.addEventListener("click", () => setColors(r.primary, r.accent));
      return b;
    }),
  );
  drawCheck();

  // ---- Actions
  const reset = el(
    "button",
    {
      type: "button",
      class: "btn ib",
      "aria-label": "Reset to default",
      "data-tip": "Reset to default",
    },
    icon("sync"),
  );
  reset.addEventListener("click", () => {
    draft = { ...draft, ...DEFAULTS, appName: "FlowCare", fontFile: null };
    name.value = draft.appName;
    drawFonts();
    for (const x of sizes.querySelectorAll<HTMLElement>("button"))
      x.setAttribute("aria-pressed", x.dataset.size === draft.textSize ? "true" : "false");
    setColors(DEFAULTS.primaryColor, DEFAULTS.accentColor);
  });
  // ---- Pull colors from your logo (mockup): read in the browser, nothing is uploaded
  const pullPick = el("input", { type: "file", id: "blogo", accept: "image/*", hidden: true });
  const pull = el(
    "button",
    { type: "button", class: "btn sm" },
    icon("file-media"),
    " Pull colors from the logo",
  );
  pull.addEventListener("click", () => pullPick.click());
  pullPick.addEventListener("change", () => {
    const file = pullPick.files?.[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const got = colorsOf(img);
      URL.revokeObjectURL(img.src);
      pullPick.value = "";
      if (!got) return toast("Couldn't find colors in that image");
      setColors(got[0], got[1]);
      toast("Colors taken from the logo", "Adjust them as needed");
    };
    img.onerror = () => toast("Couldn't read that image");
    img.src = URL.createObjectURL(file);
  });
  const link = el("a", { href: "#", tabindex: "-1" }, "A link");
  link.addEventListener("click", (e) => e.preventDefault());
  host.append(
    pageHead(
      ctx,
      el(
        "span",
        {},
        "How the app looks for ",
        el("b", {}, company),
        ". Each company using the app sets its own.",
      ),
      [status.el, reset],
      "Company settings",
    ),
    el(
      "section",
      { class: "card", "aria-label": "Top bar" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("file-media"), " Top bar"),
          el(
            "span",
            { class: "small muted" },
            "The logo and a short name show at the top left for everyone in the company.",
          ),
        ),
      ),
      el(
        "div",
        { class: "grid2", style: "gap:10px" },
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el(
            "div",
            { class: "field" },
            el("label", { for: "tb-name" }, "Short name (up to 20 characters)"),
            shortName,
          ),
          el(
            "div",
            { class: "field" },
            el("span", { class: "label" }, "Logo"),
            el(
              "div",
              { style: "display:flex;gap:12px;align-items:center;flex-wrap:wrap" },
              logoPrev,
              logoUpload?.el,
              removeLogo,
            ),
            el(
              "span",
              { class: "hint" },
              "Square works best. Without a logo, the company initials show in the main color.",
            ),
          ),
          el("div", { class: "field" }, el("span", { class: "label" }, "Show"), shows),
        ),
        el("div", {}, el("span", { class: "small muted" }, "Preview"), topPrev),
      ),
    ),
    el(
      "section",
      { class: "card" },
      el("h2", {}, icon("organization"), " App name"),
      el(
        "div",
        { class: "grid2", style: "gap:10px" },
        el(
          "div",
          { class: "field" },
          el("label", { for: "bname" }, "App name"),
          name,
          el("span", { class: "hint" }, "Shown in the browser tab and when the app is installed."),
        ),
      ),
    ),
    el(
      "div",
      { class: "grid2" },
      el(
        "section",
        { class: "card" },
        el("h2", {}, icon("typography"), " Text size"),
        el(
          "p",
          { class: "small muted" },
          "The company default. Each person can change their own under their initials, top right.",
        ),
        sizes,
        el("h2", { style: "margin-top:18px" }, icon("typography"), " Font"),
        el("div", { class: "field" }, el("label", { for: "bfont" }, "Font"), font),
        el("div", { style: "margin-top:8px" }, fontUpload?.el),
      ),
      el(
        "section",
        { class: "card" },
        el("h2", {}, icon("paintbrush"), " Colors"),
        el(
          "p",
          { class: "small muted" },
          "Buttons, links, highlights and the top bar follow these two colors.",
        ),
        el("div", { class: "grid2", style: "gap:10px" }, primary.field, accent.field),
        el("div", { class: "eyebrow", style: "margin:8px 0 6px" }, "Ready-made"),
        ready,
        el(
          "div",
          { style: "display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-top:12px" },
          pull,
          pullPick,
          el("span", { class: "hint" }, "Takes the main and accent colors from a logo image."),
        ),
        check,
      ),
    ),
    el(
      "section",
      { class: "card" },
      el(
        "div",
        { class: "cardhead" },
        el("h2", {}, icon("eye"), " Preview"),
        el("span", { class: "small muted" }, "This page already shows the choices made"),
      ),
      el(
        "div",
        { class: "bprev" },
        el(
          "div",
          { class: "actions" },
          el(
            "button",
            { type: "button", class: "btn pri", tabindex: "-1" },
            icon("plus"),
            " New request",
          ),
          el(
            "button",
            { type: "button", class: "btn", tabindex: "-1" },
            icon("download"),
            " Download",
          ),
          link,
          el("span", { class: "chip ok" }, "Active"),
          el("span", { class: "chip warn" }, "Needs review"),
          el("span", { class: "chip info" }, "Info"),
        ),
        el(
          "div",
          { class: "tw" },
          el(
            "table",
            {},
            el(
              "thead",
              {},
              el(
                "tr",
                {},
                el("th", {}, "Patient"),
                el("th", {}, "Next visit"),
                el("th", {}, "Status"),
              ),
            ),
            el(
              "tbody",
              {},
              el(
                "tr",
                {},
                el("td", {}, el("b", {}, "Pat Example")),
                el("td", {}, "Thu 9:00 AM"),
                el("td", {}, el("span", { class: "chip ok" }, "Active")),
              ),
              el(
                "tr",
                {},
                el("td", {}, el("b", {}, "Lee Sample")),
                el("td", {}, "Sat 2:30 PM"),
                el("td", {}, el("span", { class: "chip ok" }, "Active")),
              ),
            ),
          ),
        ),
      ),
    ),
    ...(activity ? [activity.el] : []),
  );
}
